import React, { useState, useEffect } from "react";
import { Form, Button, Table, Row, Col, Card, Container } from "react-bootstrap";
import { Download } from "lucide-react";
import { Link } from "react-router-dom";
import TooltipOption from "../../../core/common/tooltipOption";
import { all_routes } from "../../router/all_routes";
import { _get, _post } from "../../Utils/Helper";
import { message } from "antd";

interface Category {
  method_name: string | number | readonly string[] | undefined;
  category_name: string | number | readonly string[] | undefined;
  income_category_name: string | number | readonly string[] | undefined;
  id: string;
  name: string;
}

interface Report {
  payment_method: string;
  id: string;
  date: string;
  category: string;
  description: string;
  amount: number;
  paymentMethod: string;
}

const NewIncomeExpenses = () => {
  const [incomeCategories, setIncomeCategories] = useState<Category[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<Category[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<Category[]>([]);
  const [reports, setReports] = useState<Report[]>([]);

  // Form states
  const [incomeData, setIncomeData] = useState({
    date: "",
    category: "",
    description: "",
    amount: "",
    paymentMethod: ""
  });

  const [expenseData, setExpenseData] = useState({
    date: "",
    category: "",
    description: "",
    amount: "",
    paymentMethod: ""
  });

  // Fetch categories, payment methods, and reports
  useEffect(() => {
    fetchIncomeCategories();
    fetchExpenseCategories();
    fetchPaymentMethods();
    fetchReports();
  }, []);

  const fetchIncomeCategories = () => {
    _get(
      "get-all-income",
      (data: Category[]) => setIncomeCategories(data),
      (err) => console.error("Failed to fetch income categories:", err)
    );
  };

  const fetchExpenseCategories = () => {
    _get(
      "get-all-exposes",
      (data: Category[]) => setExpenseCategories(data),
      (err) => console.error("Failed to fetch expense categories:", err)
    );
  };

  const fetchPaymentMethods = () => {
    _get(
      "get-all-payment-methods",
      (data: Category[]) => setPaymentMethods(data),
      (err) => console.error("Failed to fetch payment methods:", err)
    );
  };

  const fetchReports = () => {
    _get(
      "get-all-reports",
      (data: Report[]) => setReports(data),
      (err) => console.error("Failed to fetch reports:", err)
    );
  };

  const handleSubmit = (data: typeof incomeData | typeof expenseData, type: "income" | "expense") => {
    if (!data.date || !data.category || !data.description || !data.amount || !data.paymentMethod) {
      message.error("Please fill out all fields.");
      return;
    }

    _post(
      "new-report",
      data,
      () => {
        message.success(`${type} report added successfully!`);
        fetchReports();
        // Reset form
        type === "income"
          ? setIncomeData({ date: "", category: "", description: "", amount: "", paymentMethod: "" })
          : setExpenseData({ date: "", category: "", description: "", amount: "", paymentMethod: "" });
      },
      (err) => {
        console.error(`Failed to submit ${type} report:`, err);
        message.error(`Failed to add ${type} report. Please try again.`);
      }
    );
  };

  const routes = all_routes;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Income and Expense Management</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Report</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  New Income & Expenses
                </li>
              </ol>
            </nav>
          </div>
          <TooltipOption />
        </div>

        <Container className="py-4">
          <Row>
            {/* Income Form */}
            <Col md={6}>
              <Card>
                <Card.Header>Add Income</Card.Header>
                <Card.Body>
                  <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(incomeData, "income"); }}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={incomeData.date}
                        onChange={(e) => setIncomeData({ ...incomeData, date: e.target.value })}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Select
                        value={incomeData.category}
                        onChange={(e) => setIncomeData({ ...incomeData, category: e.target.value })}
                        required
                      >
                        <option value="">Select Income Category</option>
                        {incomeCategories.map((cat) => (
                          <option key={cat.id} value={cat.income_category_name}>
                            {cat.income_category_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={incomeData.description}
                        onChange={(e) => setIncomeData({ ...incomeData, description: e.target.value })}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        value={incomeData.amount}
                        onChange={(e) => setIncomeData({ ...incomeData, amount: e.target.value })}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        value={incomeData.paymentMethod}
                        onChange={(e) => setIncomeData({ ...incomeData, paymentMethod: e.target.value })}
                        required
                      >
                        <option value="">Select Payment Method</option>
                        {paymentMethods.map((method) => (
                          <option key={method.id} value={method.method_name}>
                            {method.method_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Button type="submit" variant="primary">
                      Add Income
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Expense Form */}
            <Col md={6}>
              <Card>
                <Card.Header>Add Expense</Card.Header>
                <Card.Body>
                  <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(expenseData, "expense"); }}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={expenseData.date}
                        onChange={(e) => setExpenseData({ ...expenseData, date: e.target.value })}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Select
                        value={expenseData.category}
                        onChange={(e) => setExpenseData({ ...expenseData, category: e.target.value })}
                        required
                      >
                        <option value="">Select Expense Category</option>
                        {expenseCategories.map((cat) => (
                          <option key={cat.id} value={cat.category_name}>
                            {cat.category_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={expenseData.description}
                        onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        value={expenseData.amount}
                        onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        value={expenseData.paymentMethod}
                        onChange={(e) => setExpenseData({ ...expenseData, paymentMethod: e.target.value })}
                        required
                      >
                        <option value="">Select Payment Method</option>
                        {paymentMethods.map((method) => (
                          <option key={method.id} value={method.method_name}>
                            {method.method_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Button type="submit" variant="primary">
                      Add Expense
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Transaction Table */}
          <Row className="mt-4">
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span>All Reports</span>
                  <Button variant="outline-primary" size="sm">
                    <Download size={16} className="me-2" />
                    Download
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((report) => (
                        <tr key={report.id}>
                          <td>{report.date}</td>
                          <td>{report.category}</td>
                          <td>{report.description}</td>
                          <td className="text-right">{report.amount.toLocaleString()}</td>
                          <td>{report.payment_method}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default NewIncomeExpenses;
